import {
  isNorwegianBankAccountValid,
  parseAccountNumberFinland,
  parseAccountNumberSweden
} from "@stacc/bank-validation";
import { Country } from "../types/common";

/**
 * Formats a bank account number based on the specified rules.
 * Finnish account numbers are IBANs and must keep their "FI" prefix, so they
 * are only stripped of non-alphanumeric characters (and upper-cased) instead of non-digits.
 * @param accountNumber - The account number to format
 * @param removeClearingNumber - Whether to remove the 5th digit of clearing number if present
 * @param country - The country the account number belongs to
 * @returns The formatted account number
 */
export const formatAccountNumber = (
  accountNumber: string,
  removeClearingNumber: boolean,
  country?: Country
): string => {
  if (!accountNumber) {
    return "";
  }

  if (country === Country.FI) {
    return accountNumber.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  }

  // Norwegian account numbers are 11 digits with no clearing number.
  if (country === Country.NO) {
    return accountNumber.replace(/\D/g, "");
  }

  // Remove all non-digit characters
  const cleanedAccountNumber: string = accountNumber.replace(/\D/g, "");

  try {
    const parsedAccountNumber = parseAccountNumberSweden(cleanedAccountNumber);

    if (
      parsedAccountNumber.clearingNumber.length === 5 &&
      removeClearingNumber
    ) {
      // Remove the 5th clearing number when specified
      const clearingNumber: string = parsedAccountNumber.clearingNumber.slice(
        0,
        4
      );
      return `${clearingNumber}${parsedAccountNumber.accountNumber}`;
    }

    return cleanedAccountNumber;
  } catch (error) {
    // Return the cleaned account number if parsing fails
    return cleanedAccountNumber;
  }
};

export const getAccountNumber = (
  flow: any,
  type: "autogiro" | "disbursement"
) => {
  const state = flow?.flow?.data || {};
  const { autogiroAccountNumber, disbursementAccountNumber } =
    state?.caseWork?.["manual-changed-values"] || {};
  const { LeaBankWeb, leabank } = state.integrations || {};

  if (type === "autogiro" && autogiroAccountNumber) {
    return autogiroAccountNumber;
  } else if (type === "disbursement" && disbursementAccountNumber) {
    return disbursementAccountNumber;
  }

  if (leabank?.bgc?.request?.bankAccountNumber) {
    return leabank.bgc.request.bankAccountNumber;
  }

  if (LeaBankWeb?.customerInformation?.accountNumber) {
    return LeaBankWeb.customerInformation.accountNumber;
  }

  return state.agentApiApplication?.application?.bankAccountNumber;
};

export function formatAccountNumberFinland(accountNumber: string): string {
  if (!accountNumber) {
    return "";
  }

  // Remove all non-digit characters except for the leading "FI" if present
  const prefix = accountNumber.startsWith("FI") ? "FI" : "";
  const digits: string = accountNumber.replace(/[^0-9]/g, "");
  const cleanedAccountNumber = `${prefix}${digits}`;

  try {
    const parsedAccountNumber = parseAccountNumberFinland(cleanedAccountNumber);
    return parsedAccountNumber.rawAccountNumber;
  } catch (error) {
    // Return the cleaned account number if parsing fails
    return cleanedAccountNumber;
  }
}

export function getAccountNumberFinland(flow: any) {
  const state = flow?.flow?.data || {};
  const integrations = state.integrations || {};
  const accountNumber =
    integrations?.LeaBankWeb?.customerInformation?.accountNumber ||
    state.agentApiApplication?.application?.bankAccountNumber ||
    "";
  return accountNumber;
}

/**
 * Validates a Norwegian bank account number (11 digits with a MOD11 check digit).
 * Returns an error message, or null when valid.
 * @param accountNumber - The account number entered by the caseworker
 * @param fieldLabel - Human-readable field name used in the error message
 */
export function validateAccountNumberNorway(
  accountNumber: string | null | undefined,
  fieldLabel: string
): string | null {
  if (!accountNumber) {
    return `${fieldLabel} is required.`;
  }
  if (!isNorwegianBankAccountValid(accountNumber)) {
    return `${fieldLabel} is not a valid Norwegian account number (11 digits).`;
  }
  return null;
}
